/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { ExamItemCreateViewModel } from "../types/ExamItemCreateViewModel.ts";
import { examTypeListEditorViewModelSchema } from "./examTypeListEditorViewModelSchema.ts";

export const examItemCreateViewModelSchema = z.object({
  componentType: z
    .number()
    .int()
    .describe(
      "Tipo do componente da questão. Obrigatório. Enum EComponentType: `1` = SingleChoice, `2` = MultipleChoice, `3` = SingleLineText, `4` = MultilineText, `5` = Numeric, `6` = Grade, `7` = Audio, `8` = Attachment.",
    ),
  weight: z
    .number()
    .int()
    .min(0)
    .max(2147483647)
    .describe("Peso da questão na composição da nota final. Opcional.")
    .nullish(),
  cultureInfo: z
    .string()
    .min(0)
    .max(10)
    .describe(
      "Código de cultura do idioma da questão (ex: pt-BR, en-US). Obrigatório. Máximo 10 caracteres.",
    ),
  label: z
    .string()
    .min(0)
    .max(16000)
    .describe(
      "Enunciado da questão (pode conter HTML). Obrigatório. Máximo 16.000 caracteres.",
    ),
  information: z
    .string()
    .min(0)
    .max(4000)
    .describe(
      "Informação complementar exibida ao aluno junto ao enunciado. Opcional. Máximo 4.000 caracteres.",
    )
    .nullish(),
  filePath: z
    .string()
    .describe("Caminho do arquivo de mídia associado à questão. Opcional.")
    .nullish(),
  fileName: z
    .string()
    .describe("Nome do arquivo de mídia associado à questão. Opcional.")
    .nullish(),
  showLabel: z.optional(
    z
      .boolean()
      .describe("Define se o enunciado da questão será exibido ao aluno."),
  ),
  showInformation: z.optional(
    z
      .boolean()
      .describe(
        "Define se a informação complementar da questão será exibida ao aluno.",
      ),
  ),
  required: z.optional(
    z.boolean().describe("Define se a resposta à questão é obrigatória."),
  ),
  fixed: z.optional(
    z
      .boolean()
      .describe(
        "Define se a questão terá posição fixa na prova (não será randomizada).",
      ),
  ),
  pageBreak: z.optional(
    z
      .boolean()
      .describe(
        "Define se haverá quebra de página após esta questão na prova.",
      ),
  ),
  disciplineId: z
    .number()
    .int()
    .describe("ID da disciplina associada à questão. Opcional.")
    .nullish(),
  parentExamItemId: z
    .number()
    .int()
    .describe(
      "ID da questão-pai, quando esta questão for uma sub-questão. Opcional.",
    )
    .nullish(),
  personAuthorId: z
    .number()
    .int()
    .describe("ID do autor da questão. Obrigatório."),
  currentFilePath: z
    .string()
    .describe(
      "Caminho atual do arquivo de mídia, utilizado para controle de substituição. Opcional.",
    )
    .nullish(),
  hasFile: z.optional(
    z
      .boolean()
      .describe("Indica se a questão possui um arquivo de mídia associado."),
  ),
  file: z.instanceof(File).nullish(),
  randomizeAlternatives: z.optional(
    z
      .boolean()
      .describe(
        "Define se as alternativas da questão serão apresentadas em ordem aleatória.",
      ),
  ),
  isAdministrative: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a questão é de uso administrativo (não computada na nota do aluno).",
      ),
  ),
  status: z.optional(
    z
      .number()
      .int()
      .describe(
        "Status de aprovação da questão no banco de questões. Enum EExamItemStatus: `1` = Pending, `2` = Approved, `3` = Rejected.",
      ),
  ),
  level: z
    .number()
    .int()
    .describe(
      "Nível de dificuldade da questão. Opcional. Enum EExamItemLevel: `1` = Simple, `2` = Complex.",
    )
    .nullish(),
  statusJustification: z
    .string()
    .describe("Justificativa para o status de aprovação da questão. Opcional.")
    .nullish(),
  active: z.optional(
    z.boolean().describe("Define se a questão estará ativa após a criação."),
  ),
  companyId: z
    .number()
    .int()
    .describe("ID da empresa à qual a questão pertence. Opcional.")
    .nullish(),
  isEvaluation: z.optional(
    z
      .boolean()
      .describe("Indica se a questão é utilizada em provas de avaliação."),
  ),
  isAiAssisted: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o conteúdo desta questão foi revisado pela IA antes de ser salvo. Quando verdadeiro, um registro de auditoria é gravado em GA_LOG. Não é persistido na entidade.",
      ),
  ),
  maxAudioTimeInSeconds: z
    .number()
    .int()
    .describe(
      "Tempo máximo em segundos para gravação de resposta em áudio. Aplicável ao componente Audio. Opcional.",
    )
    .nullish(),
  teacherEvaluation: z
    .boolean()
    .describe(
      "Define se a questão do tipo Grade requer avaliação do professor. Aplicável ao componente Grade. Opcional.",
    )
    .nullish(),
  decimalPlaces: z
    .number()
    .int()
    .min(0)
    .max(15)
    .describe(
      "Número de casas decimais aceitas na resposta numérica. Aplicável ao componente Numeric. Valor entre 0 e 15. Opcional.",
    )
    .nullish(),
  min: z
    .number()
    .describe(
      "Valor mínimo aceito para resposta numérica. Aplicável ao componente Numeric. Opcional.",
    )
    .nullish(),
  max: z
    .number()
    .describe(
      "Valor máximo aceito para resposta numérica. Aplicável ao componente Numeric. Opcional.",
    )
    .nullish(),
  correctAnswer: z
    .string()
    .describe(
      "Resposta correta esperada para questão numérica. Aplicável ao componente Numeric. Opcional.",
    )
    .nullish(),
  maxFileSizeInMegabytes: z
    .number()
    .int()
    .min(1)
    .max(30)
    .describe(
      "Tamanho máximo em megabytes do arquivo de resposta aceito pelo aluno. Aplicável ao componente Attachment. Valor entre 1 e 30. Opcional.",
    )
    .nullish(),
  acceptedFormats: z
    .array(z.string())
    .describe(
      "Lista de extensões de arquivo aceitas como resposta. Aplicável ao componente Attachment. Opcional.",
    )
    .nullish(),
  examTypeList: z.optional(z.lazy(() => examTypeListEditorViewModelSchema)),
  subtype: z.number().int().nullish(),
  modelAnswer: z.string().min(0).max(1000).nullish(),
  keyTerms: z.string().min(0).max(1000).nullish(),
}) as unknown as ToZod<ExamItemCreateViewModel>;
